import { useState } from 'react';
import { useSeoMeta } from '@unhead/react';
import { useQueryClient } from '@tanstack/react-query';
import { useNostr } from '@nostrify/react';
import { useNavigate } from 'react-router-dom';
import { nip19 } from 'nostr-tools';
import { Input } from '@/components/ui/input';
import { FlagGenerator } from '@/components/FlagGenerator';
import { FlagCard } from '@/components/FlagCard';
import { FlagDetailDialog } from '@/components/FlagDetailDialog';
import { LoginArea } from '@/components/auth/LoginArea';
import { Skeleton } from '@/components/ui/skeleton';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useFlags } from '@/hooks/useFlags';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { getAllEphemeralKeys, getEphemeralKey, signWithEphemeralKey, removeEphemeralKey } from '@/lib/ephemeralKeys';
import { useToast } from '@/hooks/useToast';
import type { NostrEvent } from '@nostrify/nostrify';
import { Flag, Plus, X, Search, User } from 'lucide-react';

const Index = () => {
  useSeoMeta({
    title: 'Free Speech Flag Generator',
    description: 'Create and share Free Speech Flags — visual flags whose stripes encode any text or byte sequence.',
  });

  const [showGenerator, setShowGenerator] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<NostrEvent | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [editEvent, setEditEvent] = useState<NostrEvent | null>(null);
  const [profileInput, setProfileInput] = useState('');
  const [profileError, setProfileError] = useState('');
  const navigate = useNavigate();

  const { data: flags, isLoading } = useFlags(50);
  const { user } = useCurrentUser();
  const { nostr } = useNostr();
  const queryClient = useQueryClient();
  const { toast } = useToast();

  // Determine ownership: logged-in user OR ephemeral key holder
  const ephemeralKeys = getAllEphemeralKeys();
  const ephemeralPubkeys = new Set(ephemeralKeys.map(k => k.pubkey));

  function isOwner(event: NostrEvent): boolean {
    if (user && event.pubkey === user.pubkey) return true;
    return ephemeralPubkeys.has(event.pubkey);
  }

  function handleFlagClick(event: NostrEvent) {
    setSelectedEvent(event);
    setDetailOpen(true);
  }

  async function handleDelete(event: NostrEvent) {
    try {
      const dTag = event.tags.find(([n]) => n === 'd')?.[1];
      const deletionTemplate = {
        kind: 5,
        content: 'Flag deleted by author',
        tags: [
          ['e', event.id],
          ...(dTag ? [['a', `32239:${event.pubkey}:${dTag}`]] : []),
        ],
        created_at: Math.floor(Date.now() / 1000),
      };

      if (user && event.pubkey === user.pubkey) {
        const signed = await user.signer.signEvent(deletionTemplate);
        await nostr.event(signed, { signal: AbortSignal.timeout(8000) });
      } else {
        // Ephemeral key delete
        const ephKey = getEphemeralKey(dTag ?? '');
        if (!ephKey || ephKey.pubkey !== event.pubkey) {
          toast({ title: 'Cannot delete', description: 'No key found for this flag.', variant: 'destructive' });
          return;
        }
        const signed = signWithEphemeralKey(ephKey, deletionTemplate);
        await nostr.event(signed, { signal: AbortSignal.timeout(8000) });
        removeEphemeralKey(dTag ?? '');
      }

      await queryClient.invalidateQueries({ queryKey: ['flags'] });
      toast({ title: 'Flag deleted' });
    } catch (err) {
      toast({ title: 'Delete failed', description: String(err), variant: 'destructive' });
    }
  }

  function handleEdit(event: NostrEvent) {
    setEditEvent(event);
    setShowGenerator(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  function handleProfileLookup() {
    setProfileError('');
    const val = profileInput.trim();
    if (!val) return;
    // Accept npub1..., nprofile1..., or raw 64-char hex
    try {
      if (val.startsWith('npub1') || val.startsWith('nprofile1')) {
        const decoded = nip19.decode(val);
        if (decoded.type === 'npub' || decoded.type === 'nprofile') {
          navigate(`/${val}`);
          return;
        }
      }
      if (/^[0-9a-fA-F]{64}$/.test(val)) {
        const npub = nip19.npubEncode(val);
        navigate(`/${npub}`);
        return;
      }
      setProfileError('Enter a valid npub, nprofile, or 64-char hex pubkey.');
    } catch {
      setProfileError('Invalid identifier.');
    }
  }

  function handlePublished() {
    setEditEvent(null);
    setShowGenerator(false);
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border bg-card/50 backdrop-blur-sm sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <Flag className="h-6 w-6 text-primary" />
            <span className="font-bold text-lg text-foreground">Free Speech Flag</span>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant={showGenerator ? 'secondary' : 'default'}
              size="sm"
              onClick={() => { setShowGenerator(!showGenerator); setEditEvent(null); }}
            >
              {showGenerator ? (
                <><X className="h-4 w-4 mr-1" /> Close</>
              ) : (
                <><Plus className="h-4 w-4 mr-1" /> Create Flag</>
              )}
            </Button>
            <LoginArea className="max-w-40" />
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-6 space-y-8">
        {/* Hero */}
        {!showGenerator && (
          <div className="text-center py-8 space-y-3">
            <h1 className="text-4xl md:text-5xl font-extrabold tracking-tight text-foreground">
              Free Speech Flag
            </h1>
            <p className="text-lg text-muted-foreground max-w-xl mx-auto">
              Encode any text or byte sequence as a colorful vertical-stripe flag. Share it on Nostr. Censorship-resistant by design.
            </p>
            <Button size="lg" onClick={() => setShowGenerator(true)}>
              <Flag className="h-5 w-5 mr-2" />
              Create your flag
            </Button>

            {/* Profile lookup */}
            <div className="mt-6 max-w-md mx-auto">
              <p className="text-sm text-muted-foreground mb-2 flex items-center justify-center gap-1">
                <User className="h-4 w-4" /> Or view any Nostr profile as a flag
              </p>
              <div className="flex gap-2">
                <Input
                  className="font-mono text-sm"
                  placeholder="npub1… or 64-char hex pubkey"
                  value={profileInput}
                  onChange={e => { setProfileInput(e.target.value); setProfileError(''); }}
                  onKeyDown={e => e.key === 'Enter' && handleProfileLookup()}
                />
                <Button variant="secondary" onClick={handleProfileLookup} disabled={!profileInput.trim()}>
                  <Search className="h-4 w-4" />
                </Button>
              </div>
              {profileError && <p className="text-destructive text-xs mt-1">{profileError}</p>}
            </div>
          </div>
        )}

        {/* Generator */}
        {showGenerator && (
          <div className="max-w-2xl mx-auto">
            <FlagGenerator
              prefillHex={editEvent ? (editEvent.tags.find(([n]) => n === 'data')?.[1] ?? '') : undefined}
              prefillText={editEvent ? (editEvent.tags.find(([n]) => n === 'source')?.[1] ?? '') : undefined}
              prefillTitle={editEvent ? (editEvent.tags.find(([n]) => n === 'title')?.[1] ?? '') : undefined}
              prefillEncoding={editEvent ? (editEvent.tags.find(([n]) => n === 'encoding')?.[1] as 'text' | 'hex' | undefined) : undefined}
              editDTag={editEvent ? (editEvent.tags.find(([n]) => n === 'd')?.[1] ?? undefined) : undefined}
              onPublished={handlePublished}
            />
          </div>
        )}

        {/* Gallery */}
        <section>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-2xl font-bold text-foreground">Gallery</h2>
            <span className="text-sm text-muted-foreground">{flags?.length ?? 0} flags</span>
          </div>

          {isLoading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {Array.from({ length: 6 }).map((_, i) => (
                <Card key={i}>
                  <Skeleton className="w-full" style={{ aspectRatio: '8/5' }} />
                  <CardContent className="p-3">
                    <div className="flex items-center gap-2">
                      <Skeleton className="h-7 w-7 rounded-full" />
                      <div className="flex-1 space-y-1">
                        <Skeleton className="h-3 w-3/4" />
                        <Skeleton className="h-3 w-1/2" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : flags && flags.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {flags.map(event => (
                <FlagCard
                  key={event.id}
                  event={event}
                  onClick={handleFlagClick}
                  isOwned={isOwner(event)}
                />
              ))}
            </div>
          ) : (
            <Card className="border-dashed">
              <CardContent className="py-16 text-center">
                <Flag className="h-10 w-10 mx-auto mb-3 text-muted-foreground/50" />
                <p className="text-muted-foreground max-w-sm mx-auto">
                  No flags yet. Be the first to create one!
                </p>
              </CardContent>
            </Card>
          )}
        </section>

        {/* Footer */}
        <footer className="text-center text-xs text-muted-foreground py-6 border-t border-border">
          <p>
            Free Speech Flag — encoding freedom one stripe at a time.
            {' '}
            <a
              href="https://marlowe.shakespeare.wtf"
              target="_blank"
              rel="noopener noreferrer"
              className="underline hover:text-foreground transition-colors"
            >
              Vibed with Marlowe
            </a>
          </p>
        </footer>
      </main>

      {/* Detail dialog */}
      <FlagDetailDialog
        event={selectedEvent}
        open={detailOpen}
        onOpenChange={setDetailOpen}
        canDelete={selectedEvent ? isOwner(selectedEvent) : false}
        canEdit={selectedEvent ? isOwner(selectedEvent) : false}
        onDelete={handleDelete}
        onEdit={handleEdit}
      />
    </div>
  );
};

export default Index;
